import React, { useEffect, useRef, useState } from 'react';
import { networkManager } from '../../systems/NetworkManager';
import { onGameEvent } from '../../core/GameEvents';
import { usePanelPositionStore } from './panelPositionStore';

const PALETTE = {
  DARK: '#0f1b26',
  PANEL: '#1a2a38',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
};

const FONT_DATA = "'JetBrains Mono', monospace";
const PANEL_WIDTH = 440;

type TutorialState = {
  step: number;
  title: string;
  body: string;
  totalSteps: number;
  mission: 'mine' | 'kill' | null;
};

function getDefaultPos(): { x: number; y: number } {
  const x = Math.max(20, Math.min(window.innerWidth - PANEL_WIDTH - 20, window.innerWidth / 2 - PANEL_WIDTH / 2));
  return { x, y: 90 };
}

const MISSION_BADGE: Record<NonNullable<TutorialState['mission']>, { label: string; color: string }> = {
  mine: { label: 'MISSION · Mine ore', color: '#20a5a6' },
  kill: { label: 'MISSION · Destroy your target drone', color: '#dd5639' },
};

/**
 * Tutorial v2 transmission panel.
 *
 * Render gating: ONLY while the player is inside an Aura Beacon's proximity
 * radius (`tutorialBeaconRange` event from GameScene). Outside, the modal hides
 * even mid-tutorial — the player has to fly back to a beacon to read again.
 *
 * Continue / Skip Step / Prev / Skip Tutorial buttons unchanged from v1; the
 * trigger-action-unlock mechanic was removed. Mission steps are gated server-
 * side: clicking Continue when the mission isn't complete shows a hint toast
 * from the `tutorialMissionIncomplete` event.
 */
export function TutorialTransmissionPanel(): React.ReactElement | null {
  const [state, setState] = useState<TutorialState | null>(() =>
    networkManager.getLatestTutorialState()
  );
  const [inBeaconRange, setInBeaconRange] = useState<boolean>(() =>
    networkManager.getBeaconInRange()
  );
  const [isBusy, setIsBusy] = useState(false);
  const [missionHint, setMissionHint] = useState<string | null>(null);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [missionProgress, setMissionProgress] = useState(() =>
    networkManager.getLatestTutorialMissionProgress()
  );

  const defaultPos = useRef(getDefaultPos()).current;
  const { tutorialPos, tutorialMinimized, setTutorialPos, setTutorialMinimized } = usePanelPositionStore();
  const [pos, setPos] = useState(tutorialPos ?? defaultPos);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const lastPos = useRef(pos);
  lastPos.current = pos;
  const missionHintTimer = useRef<number | null>(null);

  useEffect(() => {
    const unsubState = onGameEvent('tutorialState', (data) => {
      setState(data);
      setIsBusy(false);
      setMissionHint(null);
      // New step → drop stale progress; the next 1Hz broadcast will fill it.
      setMissionProgress(null);
    });
    const unsubProgress = onGameEvent('tutorialMissionProgress', (data) => {
      setMissionProgress(data);
    });
    const unsubRange = onGameEvent('tutorialBeaconRange', ({ inRange }) => {
      setInBeaconRange(inRange);
    });
    const unsubIncomplete = onGameEvent('tutorialMissionIncomplete', ({ mission }) => {
      setIsBusy(false);
      const text = mission === 'mine'
        ? 'Mine the required ore first. Mine and return.'
        : 'Your target drone is still alive. Destroy it and return.';
      setMissionHint(text);
      if (missionHintTimer.current) window.clearTimeout(missionHintTimer.current);
      missionHintTimer.current = window.setTimeout(() => setMissionHint(null), 5000);
    });
    const unsubComplete = onGameEvent('tutorialComplete', () => {
      setState(null);
      setIsBusy(false);
    });
    return () => {
      unsubState();
      unsubProgress();
      unsubRange();
      unsubIncomplete();
      unsubComplete();
      if (missionHintTimer.current) window.clearTimeout(missionHintTimer.current);
    };
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const next = { x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y };
      lastPos.current = next;
      setPos(next);
    };
    const onUp = () => {
      if (dragging.current) {
        dragging.current = false;
        setTutorialPos(lastPos.current);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [setTutorialPos]);

  // Modal is rendered only when the player is in beacon proximity AND has an
  // active tutorial. Out of range → hidden, even mid-tutorial.
  if (!state || !inBeaconRange) return null;

  const onDragStart = (e: React.MouseEvent) => {
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };

  const resetPos = () => {
    const fresh = getDefaultPos();
    setPos(fresh);
    setTutorialPos(null);
  };

  const isFirstStep = state.step === 0;
  const isLastStep = state.step + 1 === state.totalSteps;
  const badge = state.mission ? MISSION_BADGE[state.mission] : null;
  // Mission gating: if this step has a mission, lock Continue until the
  // server reports complete. Stale progress (step mismatch) is ignored so a
  // late-arriving previous-step report doesn't unlock the current one.
  const missionLocked =
    !!state.mission &&
    (!missionProgress ||
      missionProgress.step !== state.step ||
      !missionProgress.complete);
  const continueDisabled = isBusy || missionLocked;
  const continueTitle = missionLocked
    ? state.mission === 'mine'
      ? `Mine ${missionProgress?.threshold ?? 3} ore (have ${missionProgress?.progress ?? 0})`
      : 'Destroy the target drone first'
    : undefined;

  const handleContinue = () => {
    if (isBusy) return;
    setIsBusy(true);
    networkManager.sendTutorialAdvance();
  };

  const handleSkipStep = () => {
    if (isBusy) return;
    setIsBusy(true);
    networkManager.sendTutorialAdvance({ force: true });
  };

  const handlePrev = () => {
    if (isBusy || isFirstStep) return;
    setIsBusy(true);
    networkManager.sendTutorialPrev();
  };

  const handleSkipTutorial = () => {
    if (isBusy) return;
    setShowSkipConfirm(true);
  };

  const confirmSkipTutorial = () => {
    setShowSkipConfirm(false);
    setIsBusy(true);
    networkManager.sendTutorialSkip();
  };

  const stepLabel = `Step ${state.step + 1} of ${state.totalSteps}`;

  const smallBtn = (label: string, onClick: () => void, disabled: boolean, title?: string) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        backgroundColor: 'transparent',
        border: `1px solid ${disabled ? PALETTE.DIM + '44' : PALETTE.DIM}88`,
        color: disabled ? PALETTE.DIM + '88' : PALETTE.LIGHT,
        padding: '6px 12px',
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '1px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: FONT_DATA,
        textTransform: 'uppercase',
        borderRadius: '2px',
      }}
    >
      {label}
    </button>
  );

  return (
    <>
    <div
      style={{
        position: 'absolute',
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        width: `${PANEL_WIDTH}px`,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(15, 27, 38, 0.92)',
        border: `1px solid ${PALETTE.TEAL}`,
        borderRadius: '4px',
        color: PALETTE.LIGHT,
        fontFamily: FONT_DATA,
        boxShadow: `0 0 24px rgba(32, 165, 166, 0.25)`,
        backdropFilter: 'blur(4px)',
        pointerEvents: 'auto',
        zIndex: 200,
        overflow: 'hidden',
      }}
    >
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: tutorialMinimized ? 'none' : `1px solid ${PALETTE.PANEL}`,
          backgroundColor: PALETTE.DARK,
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: PALETTE.TEAL,
              boxShadow: `0 0 6px ${PALETTE.TEAL}`,
            }}
          />
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', color: PALETTE.TEAL }}>
            TRANSMISSION
          </span>
          <span style={{ fontSize: '10px', color: PALETTE.DIM, letterSpacing: '1px', marginLeft: '4px' }}>
            {stepLabel}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <button
            onClick={(e) => { e.stopPropagation(); resetPos(); }}
            onMouseDown={(e) => e.stopPropagation()}
            title="Reset position"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${PALETTE.DIM}55`,
              borderRadius: '2px',
              color: PALETTE.DIM,
              cursor: 'pointer',
              width: '20px',
              height: '20px',
              padding: 0,
              fontSize: '11px',
              lineHeight: '18px',
              textAlign: 'center',
              fontFamily: FONT_DATA,
            }}
          >
            ⊕
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setTutorialMinimized(!tutorialMinimized); }}
            onMouseDown={(e) => e.stopPropagation()}
            title={tutorialMinimized ? 'Expand' : 'Minimize'}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${PALETTE.DIM}55`,
              borderRadius: '2px',
              color: PALETTE.LIGHT,
              cursor: 'pointer',
              width: '20px',
              height: '20px',
              padding: 0,
              fontSize: '11px',
              fontWeight: 600,
              lineHeight: '18px',
              textAlign: 'center',
              fontFamily: FONT_DATA,
            }}
          >
            {tutorialMinimized ? '+' : '_'}
          </button>
        </div>
      </div>

      {!tutorialMinimized && (
        <div style={{ padding: '14px 18px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: PALETTE.LIGHT, marginBottom: '8px', letterSpacing: '0.5px' }}>
            Aura
          </div>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: PALETTE.ORANGE,
              marginBottom: '10px',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
            }}
          >
            {state.title}
          </div>
          {badge && (
            <div
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                marginBottom: '10px',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1.2px',
                color: badge.color,
                border: `1px solid ${badge.color}88`,
                borderRadius: '2px',
                background: `${badge.color}11`,
              }}
            >
              {badge.label}
              {state.mission === 'mine' && missionProgress && missionProgress.step === state.step && (
                <span style={{ marginLeft: '8px', opacity: 0.85 }}>
                  · {missionProgress.progress}/{missionProgress.threshold}
                </span>
              )}
            </div>
          )}
          <div
            style={{
              fontSize: '12px',
              lineHeight: 1.6,
              color: PALETTE.LIGHT,
              marginBottom: '14px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {state.body}
          </div>
          {missionHint && (
            <div
              style={{
                fontSize: '11px',
                color: PALETTE.ORANGE,
                marginBottom: '12px',
                padding: '8px 10px',
                background: `${PALETTE.ORANGE}11`,
                border: `1px solid ${PALETTE.ORANGE}55`,
                borderRadius: '2px',
              }}
            >
              {missionHint}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <button
              onClick={handleSkipTutorial}
              disabled={isBusy}
              style={{
                background: 'transparent',
                border: 'none',
                color: PALETTE.DIM,
                fontSize: '10px',
                letterSpacing: '0.8px',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                padding: '4px 0',
                fontFamily: FONT_DATA,
                textTransform: 'uppercase',
              }}
              onMouseEnter={(e) => { if (!isBusy) e.currentTarget.style.color = PALETTE.ORANGE; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = PALETTE.DIM; }}
            >
              Skip Tutorial
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {smallBtn('← Prev', handlePrev, isBusy || isFirstStep, isFirstStep ? 'Already on the first step' : 'Go back one step')}
              {smallBtn('Skip Step', handleSkipStep, isBusy, 'Skip this step only')}
              <button
                onClick={handleContinue}
                disabled={continueDisabled}
                title={continueTitle}
                style={{
                  backgroundColor: continueDisabled ? PALETTE.DIM : PALETTE.TEAL,
                  color: PALETTE.DARK,
                  border: 'none',
                  padding: '8px 22px',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  cursor: continueDisabled ? 'not-allowed' : 'pointer',
                  fontFamily: FONT_DATA,
                  textTransform: 'uppercase',
                  borderRadius: '2px',
                  opacity: continueDisabled ? 0.6 : 1,
                }}
              >
                {isLastStep ? 'Finish' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {showSkipConfirm && (
      <div
        onClick={() => setShowSkipConfirm(false)}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(2px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          pointerEvents: 'auto',
          fontFamily: FONT_DATA,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '460px',
            backgroundColor: PALETTE.DARK,
            border: `1px solid ${PALETTE.ORANGE}`,
            borderRadius: '4px',
            color: PALETTE.LIGHT,
            boxShadow: `0 0 32px rgba(221, 86, 57, 0.35)`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              borderBottom: `1px solid ${PALETTE.PANEL}`,
              backgroundColor: 'rgba(221, 86, 57, 0.08)',
            }}
          >
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: PALETTE.ORANGE,
                boxShadow: `0 0 8px ${PALETTE.ORANGE}`,
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', color: PALETTE.ORANGE, textTransform: 'uppercase' }}>
              Skip Tutorial?
            </span>
          </div>
          <div style={{ padding: '18px 20px' }}>
            <div style={{ fontSize: '12px', lineHeight: 1.6, marginBottom: '6px' }}>
              This will permanently dismiss the Aura transmissions on this character. You can&apos;t restart the tutorial — make sure you&apos;re ready.
            </div>
            <div style={{ fontSize: '11px', color: PALETTE.DIM, lineHeight: 1.5 }}>
              The Academy constellation itself stays open. Use either gateway when you want to leave.
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '10px 16px 14px',
              borderTop: `1px solid ${PALETTE.PANEL}`,
            }}
          >
            <button
              onClick={() => setShowSkipConfirm(false)}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${PALETTE.DIM}88`,
                color: PALETTE.LIGHT,
                padding: '8px 18px',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '1.2px',
                cursor: 'pointer',
                fontFamily: FONT_DATA,
                textTransform: 'uppercase',
                borderRadius: '2px',
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmSkipTutorial}
              style={{
                backgroundColor: PALETTE.ORANGE,
                border: 'none',
                color: PALETTE.DARK,
                padding: '8px 22px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '1.2px',
                cursor: 'pointer',
                fontFamily: FONT_DATA,
                textTransform: 'uppercase',
                borderRadius: '2px',
              }}
            >
              Skip Tutorial
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
